import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, XCircle, Users, Wrench } from 'lucide-react';
import { useLicenseValidation } from '@/hooks/useLicenseValidation';

interface LicenseUsageAlertProps {
  clientId?: string;
  compact?: boolean;
}

export const LicenseUsageAlert = ({ clientId, compact = false }: LicenseUsageAlertProps) => {
  const { licenseUsage, loading, checkLicenseHealth } = useLicenseValidation();

  if (loading || !licenseUsage) {
    return null;
  }

  const health = checkLicenseHealth();
  const techUsagePercent = licenseUsage.max_technicians > 0 
    ? (licenseUsage.current_technicians / licenseUsage.max_technicians) * 100 
    : 0;
  
  const userUsagePercent = licenseUsage.max_users 
    ? (licenseUsage.current_users / licenseUsage.max_users) * 100 
    : 0;

  const getIcon = () => {
    switch (health.status) {
      case 'critical':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
  };

  const getVariant = () => {
    switch (health.status) {
      case 'critical':
        return 'destructive' as const;
      case 'warning':
        return 'default' as const;
      default:
        return 'default' as const;
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 p-2 border rounded-md bg-background">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {licenseUsage.client_name}
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Wrench className="h-3 w-3" />
              {licenseUsage.current_technicians}/{licenseUsage.max_technicians}
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {licenseUsage.current_users}/{licenseUsage.max_users || '∞'}
            </span>
          </div>
        </div>
        <Badge variant={health.status === 'critical' ? 'destructive' : 'secondary'} className="text-xs">
          {Math.round(Math.max(techUsagePercent, userUsagePercent))}%
        </Badge>
      </div>
    );
  }

  return (
    <Alert variant={getVariant()}>
      {getIcon()}
      <AlertTitle>Status da Licença - {licenseUsage.client_name}</AlertTitle>
      <AlertDescription>
        <div className="mt-3 space-y-3">
          {/* Técnicos */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium flex items-center gap-1">
                <Wrench className="h-4 w-4" />
                Técnicos
              </span>
              <span className="text-sm">
                {licenseUsage.current_technicians} / {licenseUsage.max_technicians}
              </span>
            </div>
            <Progress 
              value={techUsagePercent} 
              className="h-2"
              aria-label={`Uso de técnicos: ${techUsagePercent.toFixed(1)}%`}
            />
          </div>

          {/* Usuários */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium flex items-center gap-1">
                <Users className="h-4 w-4" />
                Usuários
              </span>
              <span className="text-sm">
                {licenseUsage.current_users} / {licenseUsage.max_users || '∞'}
              </span>
            </div>
            {licenseUsage.max_users && (
              <Progress 
                value={userUsagePercent} 
                className="h-2"
                aria-label={`Uso de usuários: ${userUsagePercent.toFixed(1)}%`}
              />
            )}
          </div>

          {health.status !== 'healthy' && (
            <div className="mt-2 p-2 bg-muted rounded text-sm">
              {health.message}
            </div>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};